import React, { MouseEvent } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import styled, { css } from 'styled-components';
import { Bold } from '@lib/styles';
import {
  Box,
  ButtonSize,
  CycleForewardIcon,
  GridItem,
  IconSize,
  OSmallParagraph,
  PrimaryButton,
  SecondaryButton,
  WarningTriangleIcon,
  defaultColors,
  OParagraph,
} from '@lifechurch/react-ion';
import { formatDate, recurrenceType, serviceTimeHasPast } from './Utils';
import { ServiceFieldsFragment } from '@generated/types';
import { useTranslation } from 'next-i18next';

interface ServiceTimeProps {
  service: ServiceFieldsFragment;
  isEditable: boolean;
  isPastService?: boolean;
  isOnboarding?: boolean;
}

interface ContentProps extends ServiceTimeProps {
  ref?: React.Ref<HTMLElement>;
  href?: string;
  isPastService?: boolean;
  onClick?: () => void;
}

interface StyledGridItemProps {
  isEditable: boolean;
  ref?: React.Ref<HTMLElement>;
  href?: string;
  onClick?: () => void;
}

const ServiceTitle = styled(OSmallParagraph as React.ComponentType<any>)`
  margin-inline-end: ${props => props.theme.space[3]};
`;

const TimeAndDate = styled.div`
  margin-inline-start: 0;
  margin-block-start: 0;
  margin-block-end: 0;
  margin-inline-end: 16px;
  display: flex;
  align-items: center;
`;

const uneditableCss = css`
  ${TimeAndDate},
  ${ServiceTitle} {
    color: ${props => props.theme.colors.gray50};
  }
`;

const editableCss = css`
  &:hover {
    cursor: pointer;
    background-color: ${props => props.theme.colors.action.light};

    ${TimeAndDate} {
      color: #4facf4;
    }

    ${Bold} {
      color: #4facf4;
    }

    &:after {
      display: none;
    }

    &:not(:first-child) {
      box-shadow: 0px -1px ${props => props.theme.colors.action.light};
    }
  }
`;

const StyledGridItem = styled(GridItem)<StyledGridItemProps>`
  padding: ${props => props.theme.space[5]}px ${props => props.theme.space[5]}px
    ${props => props.theme.space[5] + 1}px ${props => props.theme.space[5]}px;
  position: relative;

  &:not(:last-child):after {
    position: absolute;
    bottom: 0;
    left: ${props => props.theme.space[5]}px;
    right: ${props => props.theme.space[5]}px;
    display: block;
    height: 1px;
    content: '';
    background-color: ${props => props.theme.colors.gray10};
  }

  ${props => (!props.isEditable ? uneditableCss : editableCss)};
`;

const PastServiceLinkContainer = styled(Box)`
  grid-column: span 12;

  &:not(:last-child):after {
    position: relative;
    margin: 0 16px;
    display: block;
    height: 1px;
    content: '';
    background-color: ${props => props.theme.colors.gray10};
  }

  &:hover {
    cursor: pointer;
    background-color: ${props => props.theme.colors.action.light};

    &:not(:first-child) {
      box-shadow: 0px -1px ${props => props.theme.colors.action.light};
    }
  }
`;
const NoContentParagraph = styled(OSmallParagraph as React.ComponentType<any>)`
  color: ${props => props.theme.colors.gray50};
`;

const Content = React.forwardRef(
  ({ href, isEditable, isOnboarding, isPastService, onClick, service }: ContentProps, ref) => {
    const dateTime = formatDate(service.scheduleTime);
    const hasVideo = (service?.content?.video?.source ?? '') !== '';
    const hasContent = !!service?.content;
    const router = useRouter();
    const { t } = useTranslation(['services', 'common']);
    const serviceWithEndRepeat = service as typeof service & { endRepeat?: string | null };
    const recurrence = recurrenceType(service.recurrence, t, serviceWithEndRepeat.endRepeat);
    const isFinalService =
      !!serviceWithEndRepeat.endRepeat && service.scheduleTime === serviceWithEndRepeat.endRepeat;

    const handleClick = (e: MouseEvent): void => {
      e.preventDefault();
      e.stopPropagation();

      router.push(`/service/${service.id}/content`);
    };

    return (
      <StyledGridItem
        alignItems={'center'}
        data-testid={isEditable ? `serviceTime-editable` : `serviceTime-uneditable`}
        display={'flex'}
        gridColumn={'span 12'}
        href={href}
        isEditable={isEditable}
        justifyContent={'space-between'}
        onClick={onClick}
        p={3}
        // @ts-expect-error - FC type incompatibility with forwardRef
        ref={ref}
      >
        <Box
          alignItems={'center'}
          display={'inline-flex'}
          justifyContent={'space-between'}
          width={'100%'}
        >
          <Box>
            <TimeAndDate>
              <OParagraph display={'inline-block'}>
                <Bold data-testid={`serviceTime-${service.id}`}>{dateTime[0]}</Bold>
                &nbsp;{dateTime[1]}
              </OParagraph>
              {recurrence && (
                <Box alignItems={'center'} display={'inline-flex'} marginInlineStart={3}>
                  <CycleForewardIcon color={defaultColors.gray50} size={IconSize.SM} />
                  <OSmallParagraph
                    color={defaultColors.gray50}
                    display={'inline'}
                    marginInlineStart={2}
                  >
                    {recurrence}
                  </OSmallParagraph>
                </Box>
              )}
              {isFinalService && (
                <Box
                  alignItems={'center'}
                  bg={defaultColors.warning.light}
                  data-testid='finalServiceLabel'
                  display={'inline-flex'}
                  marginInlineStart={3}
                  px={3}
                  py={2}
                >
                  <WarningTriangleIcon color={defaultColors.warning.active} size={IconSize.SM} />
                  <OSmallParagraph color={defaultColors.warning.dark} marginInlineStart={2}>
                    {t('final_service')}
                  </OSmallParagraph>
                </Box>
              )}
            </TimeAndDate>
            {service.content && (
              <ServiceTitle data-testid='serviceContent-title' marginInlineEnd={5}>
                {service.content.title}
              </ServiceTitle>
            )}
          </Box>
        </Box>
        <Box alignItems={'center'} display={'inline-flex'} flexShrink={0}>
          {!hasVideo && !isPastService && hasContent && (
            <OSmallParagraph
              bg={defaultColors.warning.light}
              color={defaultColors.warning.dark}
              data-testid='needsVideoLabel'
              marginInlineEnd={3}
              px={3}
              py={2}
            >
              {t('needs_video')}
            </OSmallParagraph>
          )}
          {!isPastService && (
            <Link as={`/service/${service.id}/content`} href='/service/[id]/content'>
              {service.content ? (
                <SecondaryButton
                  data-testid={`editContentButton-${service.id}`}
                  size={ButtonSize.SM}
                >
                  {t('edit_content')}
                </SecondaryButton>
              ) : (
                <PrimaryButton
                  data-testid={`addContentButton-${service.id}`}
                  pulsing={isOnboarding}
                  size={ButtonSize.SM}
                >
                  {t('add_content')}
                </PrimaryButton>
              )}
            </Link>
          )}
          {isPastService && !service.content && (
            <NoContentParagraph>{t('no_content')}</NoContentParagraph>
          )}
          {isPastService && service.content && (
            <Box onClick={handleClick}>
              <SecondaryButton data-testid={`viewContentButton-${service.id}`} size={ButtonSize.SM}>
                {t('view_content')}
              </SecondaryButton>
            </Box>
          )}
        </Box>
      </StyledGridItem>
    );
  }
);
Content.displayName = 'ServiceContent';

const ServiceTime: React.FC<ServiceTimeProps> = ({
  isEditable,
  isOnboarding,
  service,
}: ServiceTimeProps) => {
  const isPastService = serviceTimeHasPast(service.endTime || service.scheduleTime);
  const router = useRouter();

  const handleClick = (e: MouseEvent): void => {
    e.preventDefault();
    router.push(`/service/[id]`, `/service/${service.id}`);
  };

  return (
    <>
      {isPastService && (
        <PastServiceLinkContainer data-testid='pastServiceLinkContainer' onClick={handleClick}>
          <Content isEditable={isEditable} isPastService={isPastService} service={service} />
        </PastServiceLinkContainer>
      )}
      {isEditable && !isPastService && (
        <Link as={`/service/${service.id}`} href='/service/[id]' passHref>
          <Content
            isEditable={isEditable}
            isOnboarding={isOnboarding}
            isPastService={isPastService}
            service={service}
          />
        </Link>
      )}
      {!isEditable && !isPastService && (
        <Content isEditable={isEditable} isPastService={isPastService} service={service} />
      )}
    </>
  );
};

export default ServiceTime;
